import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as api from '../../../api';
import { Button } from '../../../components/Button';
import { CheckBox } from '../../../components/CheckBox';
import { CheckMark, CheckMarkTypes } from '../../../components/CheckMark';
import { getOptionsFromRecord } from '../../../components/utils';
import { InfoMessage } from '../../../components/InfoMessage';
import { SelectInput } from '../../../components/SelectInput';

import LoadingModal from '../../../components/LoadingModal';
import DocumentDisplayModal from './DocumentDisplayModal';

import styles from './index.module.css';
import defaultStyles from '../../../styles/formSetStyles.module.css';

export enum SigningFor {
  Myself = 'myself',
  Guardian = 'guardian',
}

export const DocumentList: React.FC<{
  documents: api.PrevisitDocument.DocumentWithTemplate[];
  handleSkip: () => void;
  hasSignature: boolean;
  onChangeSigningFor: (value: SigningFor) => void;
  onClickDisclosures: () => void;
  onClickSign: () => void;
  selectedDocuments: string[];
  setSelectedDocuments: (updatedList: string[]) => void;
  signingFor: SigningFor | null;
  guardianRelationship: string;
  onGuardianRelationshipUpdate: (value: string) => void;
  pending?: boolean;
}> = (props) => {
  const {
    documents,
    handleSkip,
    hasSignature,
    onChangeSigningFor,
    onClickDisclosures,
    onClickSign,
    selectedDocuments,
    setSelectedDocuments,
    signingFor,
    guardianRelationship,
    onGuardianRelationshipUpdate,
    pending,
  } = props;
  const { t } = useTranslation();
  const [textError, setTextError] = useState('');
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [modalTitle, setModalTitle] = useState<string | null>(null);

  const canAddSignature =
    signingFor !== null &&
    (signingFor === SigningFor.Myself || guardianRelationship.length);

  const handleClickDisclosures = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      onClickDisclosures();
    },
    [onClickDisclosures],
  );

  return (
    <>
      <LoadingModal isOpen={!!pending} />
      <DocumentDisplayModal
        documentUrl={documentUrl || ''}
        isOpen={documentUrl !== null}
        headerText={t(modalTitle || '')}
        onRequestClose={() => {
          setModalTitle(null);
          setDocumentUrl(null);
        }}
      />
      <div className={styles.QuestionForm}>
        {textError && <InfoMessage isError={true}>{textError}</InfoMessage>}
        <div className={defaultStyles.Label}>
          {t('Please review the following document(s).')}
        </div>
        <div className={defaultStyles.Label}>
          {t(
            'Tap the checkmark next to each document to sign it. Click the name of the document to view it.',
          )}
        </div>

        <div className={styles.DocumentList}>
          {documents.map((document, i) => {
            const documentTemplateName = t(document.templateName);
            return (
              <div key={i} className={styles.DocumentItem}>
                <CheckBox
                  checked={selectedDocuments.includes(document.id)}
                  label={
                    <div
                      className={styles.DocumentTitle}
                      onClick={() => {
                        setDocumentUrl(
                          `${api.client.common.constructPath(
                            api.client.PrevisitDocumentTemplate.getPreviewUrl(
                              document,
                            ),
                          )}`,
                        );
                        setModalTitle(documentTemplateName);
                      }}
                    >
                      {document.required
                        ? `${documentTemplateName} (${t('REQUIRED')})`
                        : documentTemplateName}
                    </div>
                  }
                  saveFieldValue={(boolString) => {
                    setTextError('');
                    const toSign = JSON.parse(boolString);

                    if (toSign) {
                      setSelectedDocuments([document.id, ...selectedDocuments]);
                      return;
                    }

                    setSelectedDocuments(
                      selectedDocuments.filter(
                        (documentId) => documentId !== document.id,
                      ),
                    );
                  }}
                  updateFieldValue={() => {}}
                  disableLabelClick={true}
                />
              </div>
            );
          })}
        </div>
        {selectedDocuments.length ? (
          <>
            <div className={`${defaultStyles.Label} ${styles.SigningForLabel}`}>
              {t('I am signing for...')}
            </div>
            <CheckMark
              label={
                <>
                  <b>{t('Myself')}</b> ({t('I am the patient')})
                </>
              }
              checked={signingFor === SigningFor.Myself}
              onClick={() => {
                onChangeSigningFor(SigningFor.Myself);
              }}
              type={CheckMarkTypes.RADIO}
            />
            <CheckMark
              label={
                <>
                  <b>{t('On behalf of the patient')}</b> (
                  {t(
                    'I am the parent, legal guardian, or person with legal authority to make health care decisions on behalf of the patient',
                  )}
                  )
                </>
              }
              checked={signingFor === SigningFor.Guardian}
              onClick={() => {
                onChangeSigningFor(SigningFor.Guardian);
              }}
              type={CheckMarkTypes.RADIO}
            />
            {signingFor === SigningFor.Guardian && (
              <div className={styles.MarginLeft}>
                <label className={styles.SelectLabel}>
                  {t('Relationship to the patient')}:
                </label>
                <SelectInput
                  onChange={onGuardianRelationshipUpdate}
                  options={getOptionsFromRecord(api.Previsit.relationshipMap)}
                  value={guardianRelationship}
                />
              </div>
            )}
          </>
        ) : (
          <></>
        )}
        <p className={styles.Disclosure}>
          {t('addSignatureDisclaimerP1')}
          <a href="/#" onClick={handleClickDisclosures}>
            <strong>{t('Electronic Record and Signature Disclosures')}</strong>
          </a>{' '}
          {t('and understand my options.')}
        </p>
      </div>

      <div className={defaultStyles.ButtonMarginWrapper}>
        {selectedDocuments.length ? (
          <Button
            children={hasSignature ? t('Edit Signature') : t('Add Signature')}
            onClick={() => {
              const hasUnselectedRequiredDocuments = documents.filter(
                ({ id, required }) =>
                  required && !selectedDocuments.includes(id),
              ).length;
              if (hasUnselectedRequiredDocuments) {
                setTextError(
                  t(
                    'Please review the required documents and mark them as checked.',
                  ),
                );
                window.scrollTo(0, 0);
                return;
              }
              if (!canAddSignature) {
                setTextError(t("Select who's signing."));
                window.scrollTo(0, 0);
              } else {
                onClickSign();
              }
            }}
            type="button"
          />
        ) : (
          <div className={styles.ContinueButton}>
            <Button
              children={hasSignature ? t('Edit Signature') : t('Add Signature')}
              onClick={() =>
                setTextError(t('Select which documents you wish to sign.'))
              }
              type="button"
            />
            {documents.some(({ required }) => required) ? (
              <></>
            ) : (
              <Button
                children={t('Continue without signing')}
                onClick={handleSkip}
                type="button"
                invertColors={true}
                disabled={pending}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
